// @ts-nocheck
// nocheck is used to avoid type errors in the theme extension file, as it may contain custom properties that are not recognized by TypeScript.
// TODO: add types for custom properties in the theme extension file, and remove @ts-nocheck when the types are added.
// types
import { alpha, ThemeOptions } from '@mui/material';
import { green, yellow } from '@mui/material/colors';

// ! Breakpoints in Figma & SDB UI theme
/*
  compact 0 - 599 (xs 0 - 599)
  medium 600 - 839 (sm 600 - 899)
  expanded 840 - 1199 (md 900 - 1199)
  large 1200 - 1599 (lg 1200 - 1535)
  extralarge 1600 + (xl 1535 +)
*/

// planning Theme extension
export const themeExtension: ThemeOptions = {
  components: {
    MuiButton: {
      // Todo: add 'tonal' variant in SDB UI theme
      variants: [
        {
          props: { variant: 'tonal' },
          style: ({ theme }) => ({
            background: theme.vars.palette.secondary.container,
            border: `1px solid ${theme.vars.palette.secondary.container}`,
            padding: theme.spacing(2, 5.25),
            color: theme.vars.palette.secondary.onContainer,
            '& .MuiTouchRipple-root': {
              transition:
                'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
            },
            '&:hover .MuiTouchRipple-root': {
              background: `color-mix(in srgb, ${theme.vars.palette.secondary.onContainer} 8%, transparent)`,
              boxShadow: theme.shadows[1]
            },
            '&:active .MuiTouchRipple-root': {
              background: `color-mix(in srgb, ${theme.vars.palette.secondary.onContainer} 12%, transparent)`,
              boxShadow: theme.shadows[1]
            },
            '&:disabled': {
              backgroundColor: theme.vars.palette.secondary.container,
              color: theme.vars.palette.surface.onSurface
            }
          })
        }
      ]
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: `0px 0px 2px 1px ${theme.palette.divider}`,
          zIndex: 1,

          '& .MuiTooltip-arrow': {
            filter: `drop-shadow(0px -1px 0px ${theme.palette.grey[400]}) !important`
          }
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.background.paper
        })
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          marginLeft: theme.spacing(2)
        })
      }
    },
    MuiModal: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: `${theme.zIndex.modal + 50} !important`
        })
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: () => ({
          transform: 'none'
        })
      }
    },
    MuiPopover: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: `${theme.zIndex.modal + 60} !important`
        })
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: `${theme.zIndex.modal + 60} !important`
        })
      }
    },
    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: `${theme.zIndex.modal + 50} !important`
        })
      }
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius / 4
        })
      }
    },
    MuiDesktopTimePicker: {
      defaultProps: {
        slotProps: { actionBar: { actions: ['nextOrAccept'] } }
      }
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: () => ({
          '& .MuiPickersLayout-actionBar': {
            justifyContent: 'center'
          },

          '& .MuiMultiSectionDigitalClockSection-root': {
            scrollbarWidth: 'none'
          }
        })
      }
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: `${theme.zIndex.modal + 60} !important`,

          '& .MuiPickersLayout-actionBar': {
            padding: theme.spacing(0, 4, 4)
          },

          '& .MuiPickersArrowSwitcher-root button': {
            marginLeft: theme.spacing(1)
          }
        })
      }
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPickersDay-root, & .MuiDayCalendar-weekDayLabel': {
            margin: 0
          },
          '& .MuiDayCalendar-weekContainer': {
            margin: 0
          },
          '& .MuiDayCalendar-weekNumberLabel, & .MuiDayCalendar-weekNumber': {
            borderRight: `1px solid ${theme.palette.divider}`,
            marginRight: theme.spacing(2)
          },
          '& .MuiPickersArrowSwitcher-spacer': {
            width: theme.spacing(6)
          },
          '& .MuiButtonBase-root.MuiPickersDay-root.MuiPickersDay-today': {
            border: `1px solid ${theme.palette.divider}`
          }
        })
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSlider-valueLabel': {
            backgroundColor: 'unset',
            color: theme.palette.text.primary,
            fontSize: theme.typography.htmlFontSize,
            top: -theme.spacing(4)
          }
        })
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-scrollbar--vertical': {
            top: theme.spacing(14.5)
          }
        })
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          variant: 'standard',
          color: theme.palette.text.primary,
          '&.read_mode': {
            color: theme.palette.text.primary,
            textFillColor: theme.palette.text.primary,
            '*': {
              color: theme.palette.text.primary,
              textFillColor: theme.palette.text.primary
            },
            '.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
              color: theme.palette.text.primary,
              textFillColor: theme.palette.text.primary
            },
            '.MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'transparent'
              },
            '&.Mui-disabled, .Mui-disabled, .Mui-disabled:before': {
              background: 'none',
              border: theme.spacing(0),
              borderColor: 'transparent'
            },
            '.MuiInput-input': {
              color: theme.palette.text.primary,
              textFillColor: theme.palette.text.primary
            }
          }
        })
      },
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          variant: 'standard',
          color: theme.palette.text.primary
        })
      },
      defaultProps: {
        variant: 'standard'
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: ({ theme }) => ({
          zIndex: theme.zIndex.snackbar
        })
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: 'auto',
          height: 'auto'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiPaper-root': {
            boxShadow: `0 1px 2px 0px ${alpha(
              theme.palette.common.black,
              0.24
            )}`
          }
        })
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: '8px 8px 2px 6px'
        },

        track: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.primary.light, 0.3),
          borderRadius: theme.shape.borderRadius,
          width: theme.spacing(13),
          height: theme.spacing(6),
          boxSizing: 'border-box',
          opacity: 1,
          border: `2px solid ${theme.vars.palette.secondary.fixedDim}`
        }),

        thumb: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper
        }),

        switchBase: ({ theme }) => ({
          padding: theme.spacing(2),
          top: theme.spacing(0.5),

          '&.Mui-checked': {
            padding: theme.spacing(2),

            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              border: `2px solid ${theme.palette.primary.main}`,
              opacity: 1
            }
          }
        })
      }
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(4, 4, 0, 0)
        })
      }
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: () => ({
          '&::after': {
            height: 'auto'
          }
        })
      }
    }
  },
  custom: {
    constants: {
      MRP: {
        navigationBar: {
          height: 50
        },
        palette: {
          employee: '#336fc4',
          client: '#ef7676',
          resource: '#b04d8d',
          workplace: '#d99d10'
        }
      },
      // The two roster value-badge hues that do not come from the semantic palette. A calmer green
      // than the theme's vivid `success`, and a yellow rather than the orange the theme's `warning`
      // is — sourced from MUI's own colour scales and kept here so the badge reads them from the
      // theme rather than importing `@mui/material/colors` in a shared component. Each is a light
      // `fill` under a darker `line`, matching the error/info pairs the badge takes from the palette.
      rosterBadge: {
        met: {
          line: green[800],
          fill: green[100]
        },
        surplus: {
          line: yellow[900],
          fill: yellow[100]
        }
      }
    },
    ReactBigCalendar: {
      eventsContainer: {
        breakpoints: {
          compact: 100,
          medium: 200,
          large: 300
        }
      }
    }
  }
};
